import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import { WHATSAPP_ENABLED } from "../lib/whatsapp";
import { OFFICES, OfficeCard } from "../components/ContactGlobal";
import { api } from "../lib/api";
import { useI18n } from "../i18n";

/**
 * Contact — dedicated page that displays the four LATAM office cards
 * (México, Colombia, Argentina, Brasil) together with an invitation
 * headline / short copy. Uses the same OfficeCard component that renders
 * inside the home Xega Hub, so any future style tweak stays in sync.
 */
export default function Contact() {
  const { pick } = useI18n();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data?.data || null)).catch(() => {});
    api.post("/track/visit", { path: "/contacto" }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen text-white" data-testid="contact-page">
      <NavBar />

      {/* Ambient backdrop */}
      <div className="relative pt-24 sm:pt-28 md:pt-32 pb-16 sm:pb-20 md:pb-24 overflow-hidden">
        <div className="absolute top-0 -left-40 w-[520px] h-[520px] rounded-full bg-cyan-500/10 blur-[150px] pointer-events-none" />
        <div className="absolute bottom-0 -right-40 w-[520px] h-[520px] rounded-full bg-emerald-400/6 blur-[150px] pointer-events-none" />
        <div className="absolute inset-0 grid-bg opacity-[0.08] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="max-w-3xl mx-auto text-center mb-10 md:mb-14"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/10 backdrop-blur mb-5">
              <span className="pulse-dot" />
              <span className="label-mono !text-cyan-300 !tracking-[0.35em] text-[10px]">
                {pick("CONTACTO · 24/7", "CONTACT · 24/7", "CONTATO · 24/7")}
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight">
              <span className="chrome-text">
                {pick("Estamos a un mensaje de distancia.", "We're one message away.", "Estamos a uma mensagem de distância.")}
              </span>
            </h1>
            <p className="mt-5 text-white/75 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              {WHATSAPP_ENABLED ? pick(
                "Escoge tu país y escríbenos por WhatsApp o llámanos directo — atención inmediata, en tu idioma, las 24 horas del día, los 365 días del año.",
                "Pick your country and message us on WhatsApp or call us directly — immediate response in your language, 24/7, all year long.",
                "Escolha seu país e fale conosco pelo WhatsApp ou ligue diretamente — atendimento imediato, no seu idioma, 24 horas por dia, 365 dias por ano."
              ) : pick(
                "Escoge tu país y llámanos directo — atención inmediata, en tu idioma, las 24 horas del día, los 365 días del año.",
                "Pick your country and call us directly — immediate response in your language, 24/7, all year long.",
                "Escolha seu país e ligue diretamente — atendimento imediato, no seu idioma, 24 horas por dia, 365 dias por ano."
              )}
            </p>
          </motion.div>

          {/* Office cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {OFFICES.map((o, i) => (
              <OfficeCard key={o.id} office={o} idx={i} />
            ))}
          </div>

          {/* Small trust line */}
          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 md:mt-14 text-center"
          >
            <p className="text-white/60 text-sm max-w-2xl mx-auto leading-relaxed">
              {pick(
                "Nuestro equipo de operadores humanos — no algoritmos — te responderá en minutos. Cada país cuenta con su propia central de atención local.",
                "Our team of human operators — not algorithms — will reply within minutes. Every country has its own local support desk.",
                "Nossa equipe de operadores humanos — não algoritmos — responde em minutos. Cada país conta com sua própria central de atendimento local."
              )}
            </p>
          </motion.div>
        </div>
      </div>

      <Footer content={content} />
      <FloatingWhatsApp phone={content?.contact?.whatsapp} />
    </div>
  );
}
